import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import './LoginScreen.css'

/**
 * The landing page for a Supabase password-recovery link (`/auth/reset-password`).
 *
 * This route did not exist. Since every user provisioned through Admin →
 * Users & Permissions is created with a random UUID password and told to use
 * "Forgot password", the reset link was the *only* way in for them — and it led
 * to a 404. In practice no admin-created user could ever log in.
 *
 * Supabase puts the recovery token in the URL fragment and exchanges it for a
 * session automatically, so by the time this renders there is usually a session
 * whose only legitimate use is setting a new password.
 *
 * 22 September — why this screen no longer trusts `getSession()` on its own.
 *
 * An expired invitation was opened in a browser where an administrator was already signed in.
 * Supabase did exactly the right thing: it redirected here with
 * `#error=access_denied&error_code=otp_expired`, and no session was created. But `getSession()`
 * returned the ADMINISTRATOR'S session, still sitting in local storage — so this screen decided
 * the link was good and offered "Choose a password". Submitting it would have changed the
 * administrator's own password, silently, on a link that belonged to somebody else and had
 * already died.
 *
 * So two things are checked now, not one:
 *
 *   - the fragment the page actually arrived with, captured at module load, before supabase-js
 *     has finished with the URL and stripped it. An `error` in it is shown as an expired link
 *     whatever session happens to exist.
 *   - that we arrived from a link at all. A session with no landing fragment is somebody who is
 *     already signed in and reached this URL another way; that is not a password reset, and the
 *     safe answer is to refuse rather than to offer a form.
 *
 * And the form now says whose account it is about to change, because the failure above was
 * invisible precisely because it never said.
 */

/* Read once, at module load. supabase-js processes the URL asynchronously after createClient,
   so this still sees the fragment the browser arrived with. */
const LANDING = new URLSearchParams(
  (typeof window !== 'undefined' ? window.location.hash : '').replace(/^#/, '')
)
const LINK_ERROR = LANDING.get('error_description')?.replace(/\+/g, ' ') || LANDING.get('error') || ''
const ARRIVED_FROM_LINK =
  LANDING.has('access_token') || LANDING.has('error') || LANDING.get('type') === 'recovery'

export default function SetNewPasswordScreen({ onDone }: { onDone: () => void }) {
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [ready, setReady] = useState(false)
  const [checking, setChecking] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)
  const [account, setAccount] = useState('')

  useEffect(() => {
    let cancelled = false

    // A link that came back with an error never made a session, and any session that is here
    // belongs to somebody else. Nothing to wait for.
    if (LINK_ERROR) {
      setReady(false)
      setChecking(false)
      return
    }

    // The token exchange can land either before or after this component mounts,
    // so check now and also listen for it.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!cancelled && session) {
        setReady(ARRIVED_FROM_LINK)
        setAccount(session.user?.email || '')
        setChecking(false)
      }
    })

    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return
      setReady(ARRIVED_FROM_LINK && !!data.session)
      setAccount(data.session?.user?.email || '')
      setChecking(false)
    })

    return () => {
      cancelled = true
      sub?.subscription?.unsubscribe()
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (password.length < 8) {
      setError('Use at least 8 characters.')
      return
    }
    if (password !== confirm) {
      setError('The two passwords do not match.')
      return
    }

    setLoading(true)
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password })
      if (updateError) throw updateError
      setDone(true)
      // Sign out so they log in fresh with the password they just chose, rather
      // than continuing on the recovery session.
      await supabase.auth.signOut()
      window.setTimeout(onDone, 2500)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not set your password')
    } finally {
      setLoading(false)
    }
  }

  if (checking) {
    return (
      <div className="auth-container">
        <div className="auth-box">
          <div className="auth-header">
            <h1>DirectAuto</h1>
            <p>Checking your link…</p>
          </div>
        </div>
      </div>
    )
  }

  if (done) {
    return (
      <div className="auth-container">
        <div className="auth-box">
          <div className="auth-header">
            <h1>DirectAuto</h1>
            <p>Password set</p>
          </div>
          <div className="success-message">
            You can now sign in with your new password. Taking you to the login screen…
          </div>
        </div>
      </div>
    )
  }

  if (!ready) {
    return (
      <div className="auth-container">
        <div className="auth-box">
          <div className="auth-header">
            <h1>DirectAuto</h1>
            <p>This link has expired</p>
          </div>
          <div className="error-message">
            {LINK_ERROR
              ? `${LINK_ERROR}. `
              : 'This link has already been used, or it was not opened from the email. '}
            These links are single-use and time-limited. Ask whoever set you up to send another
            one, or use “Forgot password” on the sign-in screen — and open the most recent email.
          </div>
          <button className="auth-button" onClick={onDone}>Back to sign in</button>
        </div>
      </div>
    )
  }

  return (
    <div className="auth-container">
      <div className="auth-box">
        <div className="auth-header">
          <h1>DirectAuto</h1>
          {/* Whose password this is about to become. The bug this replaces was invisible
              exactly because the screen never said. */}
          <p>{account ? `Choose a password for ${account}` : 'Choose a password'}</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="new-password">New password</label>
            <input
              id="new-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              autoComplete="new-password"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirm-password">Confirm password</label>
            <input
              id="confirm-password"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Type it again"
              autoComplete="new-password"
              required
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button type="submit" className="auth-button" disabled={loading}>
            {loading ? 'Saving…' : 'Set password and continue'}
          </button>
        </form>

        <div className="auth-footer">
          <button className="link-button" onClick={onDone}>Back to sign in</button>
        </div>
      </div>
    </div>
  )
}
